import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';

import { ModalHeader } from 'shared/ModalHeader';
import { useCloseLead } from 'modules/lead/hooks/useCloseLead';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { titles, types } from 'constants/dropdownOptions';
import { createContact } from 'services/contact';
import { useReferredToContact } from 'hooks/useReferredToContact';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

export default function ReferredOutDialog({ onHide, leadDetails, onSuccess }) {
  const [selectedContactType, setSelectedContactType] = useState('');
  const createdContactRef = useRef(null);

  const { searchAsync, setResults: setContactList } = useReferredToContact(leadDetails?.is_lead || false, 'person', true);

  const { control, trigger, setValue, getValues, saveLoading, errors, showCustomReferral, setShowCustomReferral } = useCloseLead(
    leadDetails,
    onHide
  );

  // Set showCustomReferral to true initially for this dialog
  useEffect(() => {
    setShowCustomReferral(true);
  }, [setShowCustomReferral]);

  const { addToast } = useToast();

  const RequiredValidation = ({ field, message = 'Required' }) => {
    return errors[field] ? <InputErrorMessage>{errors[field]?.message || message}</InputErrorMessage> : null;
  };

  const handleSaveReferral = async () => {
    // Validate required fields
    const fieldsToValidate = ['referral_contact_type'];

    if (!selectedContactType) {
      const isValid = await trigger(['referral_contact_type']);
      if (!isValid) return;
      return;
    }

    if (selectedContactType === 'person') {
      fieldsToValidate.push('referral_title', 'referral_first_name', 'referral_last_name');
    } else if (selectedContactType === 'business') {
      fieldsToValidate.push('referral_business_name');
    }

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    // Create the contact
    await handleCreateReferralContact();

    // Check if contact was created successfully
    const selectedContact = getValues('old_referred_to');
    if (selectedContact) {
      onSuccess(selectedContact, createdContactRef.current);
      onHide();
    }
  };

  const handleCreateReferralContact = async () => {
    if (showCustomReferral) {
      if (!selectedContactType) {
        const isValid = await trigger(['referral_contact_type']);
        if (!isValid) return;
        return; // Don't proceed if no contact type is selected
      }

      const fieldsToValidate =
        selectedContactType === 'person' ? ['referral_title', 'referral_first_name', 'referral_last_name'] : ['referral_business_name'];

      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;
    }

    const values = getValues();
    let data = {};

    if (selectedContactType === 'person') {
      data = {
        contact_type: 'person',
        title: values?.referral_title,
        first_name: values?.referral_first_name,
        last_name: values?.referral_last_name,
        is_referral_entity: true,
        referral_values: ['INBOUND'],
      };
    } else {
      data = {
        contact_type: 'business',
        title: values?.referral_business_name,
        is_referral_entity: true,
        referral_values: ['INBOUND'],
      };
    }

    try {
      const response = await createContact(data);

      if (response?.data) {
        const newContact = response?.data;
        createdContactRef.current = newContact;

        setContactList((prevList) => {
          const newList = [...prevList, newContact];

          return newList;
        });

        setValue('old_referred_to', response?.data?.contact_id);
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          toastConstant.message.REFERRED_TO_CONTACT_CREATE_SUCCESS
        );
      }

      // Clear form fields based on contact type
      if (selectedContactType === 'person') {
        setValue('referral_title', '');
        setValue('referral_first_name', '');
        setValue('referral_last_name', '');
      } else {
        setValue('referral_business_name', '');
      }

      setShowCustomReferral(false);
    } catch (error) {
      console.error('Error creating referral contact:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.REFERRED_TO_CONTACT_CREATE_FAILED);
    }
  };

  useEffect(() => {
    searchAsync({ query: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails]);

  return (
    <Dialog visible onHide={onHide} className="new-task-popup" header={ModalHeader} footer={<></>}>
      <div className="d-flex justify-content-between align-items-center mb-3 w-100">
        <div className="modal-heading F-size16 text-bold">Create Referral</div>
      </div>

      {/* Referred Out Section */}
      {
        <div className="d-flex align-items-center flex-wrap py-2 p-3 my-2">
          <div className="w-100">
            <div className="mb-2"></div>

            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="w-100">
                  <div className="d-flex py-2 flex-wrap align-items-start">
                    <label className="col-sm-4 col-12 p-medium mt-2">
                      Contact Type <span className="alert-star">*</span>
                    </label>
                    <div className="col-sm-8 col-12">
                      <Controller
                        name="referral_contact_type"
                        control={control}
                        rules={{ required: showCustomReferral ? 'Please select a contact type' : false }}
                        render={({ field: { name, value, onChange } }) => (
                          <Dropdown
                            id={name}
                            options={types.filter((type) => type.value !== 'location')}
                            onChange={(e) => {
                              onChange(e.target.value);
                              setSelectedContactType(e.target.value);
                              // Clear form fields when contact type changes
                              setValue('referral_title', '');
                              setValue('referral_first_name', '');
                              setValue('referral_last_name', '');
                              setValue('referral_business_name', '');
                              trigger('referral_contact_type');
                            }}
                            value={value}
                            placeholder="Select Contact Type"
                            className="input-shadow w-100"
                            filter
                          />
                        )}
                      />
                      <RequiredValidation field={'referral_contact_type'} />
                    </div>
                  </div>

                  {selectedContactType && (
                    <>
                      {selectedContactType === 'person' ? (
                        <>
                          <div className="d-flex py-2 flex-wrap align-items-start">
                            <label className="col-sm-4 col-12 p-medium mt-2">
                              Title <span className="alert-star">*</span>
                            </label>
                            <div className="col-sm-8 col-12">
                              <Controller
                                name="referral_title"
                                control={control}
                                rules={{ required: showCustomReferral ? 'Required' : false }}
                                render={({ field: { name, value, onChange } }) => (
                                  <Dropdown
                                    id={name}
                                    options={titles}
                                    onChange={(e) => {
                                      onChange(e.target.value);
                                      trigger('referral_title');
                                    }}
                                    value={value}
                                    placeholder="Title"
                                    className="w-100"
                                    filter
                                  />
                                )}
                              />
                              <RequiredValidation field={'referral_title'} />
                            </div>
                          </div>

                          <div className="d-flex py-2 flex-wrap align-items-start">
                            <label className="col-sm-4 col-12 p-medium mt-2">
                              First Name <span className="alert-star">*</span>
                            </label>
                            <div className="col-sm-8 col-12">
                              <Controller
                                name="referral_first_name"
                                control={control}
                                rules={{
                                  required: showCustomReferral ? 'Required' : false,
                                  pattern: {
                                    value: /^[aA-zZ\s'-]+$/,
                                    message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                  },
                                }}
                                render={({ field }) => (
                                  <TextSnippetWrapper
                                    {...field}
                                    type="text"
                                    value={field.value}
                                    placeholder="First Name"
                                    onChange={(e) => {
                                      const trimmedValue = e.target.value.trimStart();
                                      field.onChange(trimmedValue);
                                      trigger('referral_first_name');
                                    }}
                                    className="input-shadow w-100"
                                  />
                                )}
                              />
                              <RequiredValidation field={'referral_first_name'} />
                            </div>
                          </div>

                          <div className="d-flex py-2 flex-wrap align-items-start">
                            <label className="col-sm-4 col-12 p-medium mt-2">
                              Last Name <span className="alert-star">*</span>
                            </label>
                            <div className="col-sm-8 col-12">
                              <Controller
                                name="referral_last_name"
                                control={control}
                                rules={{
                                  required: showCustomReferral ? 'Required' : false,
                                  pattern: {
                                    value: /^[aA-zZ\s'-]+$/,
                                    message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                  },
                                }}
                                render={({ field }) => (
                                  <TextSnippetWrapper
                                    type="text"
                                    value={field.value}
                                    placeholder="Last Name"
                                    onChange={(e) => {
                                      const trimmedValue = e.target.value.trimStart();
                                      field.onChange(trimmedValue);
                                      trigger('referral_last_name');
                                    }}
                                    className="input-shadow w-100"
                                    inputRef={field.ref}
                                  />
                                )}
                              />
                              <RequiredValidation field={'referral_last_name'} />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="d-flex py-2 flex-wrap align-items-start">
                            <label className="col-sm-4 col-12 p-medium mt-2">
                              Business Name <span className="alert-star">*</span>
                            </label>
                            <div className="col-sm-8 col-12">
                              <Controller
                                name="referral_business_name"
                                control={control}
                                rules={{ required: showCustomReferral ? 'Required' : false }}
                                render={({ field }) => (
                                  <TextSnippetWrapper
                                    {...field}
                                    type="text"
                                    value={field.value}
                                    placeholder="Enter Business Name"
                                    onChange={(e) => {
                                      const trimmedValue = e.target.value.trimStart();
                                      field.onChange(trimmedValue);
                                      trigger('referral_business_name');
                                    }}
                                    className="input-shadow w-100"
                                  />
                                )}
                              />
                              <RequiredValidation field={'referral_business_name'} />
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <RequiredValidation field="old_referred_to" />
          </div>
        </div>
      }

      {/* Footer Buttons */}
      <div className="footer-btn">
        <Button className="p-button-secondary outline me-2" label="Cancel" onClick={onHide} disabled={saveLoading} />
        <Button className="p-button-secondary" label="Save" onClick={handleSaveReferral} disabled={saveLoading} />
      </div>
    </Dialog>
  );
}
